'use client';

import React from 'react';
import { Tooltip } from 'react-tooltip';

import { useAppSelector } from 'src/hooks/redux-hooks';
import { BuildBackFlagIcon } from 'src/assets/images/svg-icons';
import type { FlagIndicator } from 'src/types/jobs';

interface FlagActivityIndicatorProps {
	jobId: number;
}

/**
 * Aggregated per-job activity flag (SAAS-815 BBR). Reads the active-flag
 * indicators map off the shared flag summary and renders a single pill:
 *  - inactive (no active flags): muted grey, no tooltip, but always present so
 *    the column keeps a stable width across rows;
 *  - active (>= 1 active flag): red pill whose hover tooltip lists each active
 *    flag name on its own line, oldest-first (the BE pre-sorts the list).
 *
 * The indicators ride the `/api/flags/summary` payload, so there is no retry /
 * backoff here — a minimal load-state pill is enough while the summary lands.
 */
const FlagActivityIndicator = ({ jobId }: FlagActivityIndicatorProps) => {
	const indicators = useAppSelector(
		(state) => state?.flagSummary?.indicators?.[jobId],
	);
	const isLoading = useAppSelector((state) => state?.flagSummary?.isLoading);

	const activeFlags: FlagIndicator[] = Array.isArray(indicators)
		? indicators
		: [];
	const isActive = activeFlags.length > 0;
	const isPending = !!isLoading && activeFlags.length === 0;
	const tooltipId = `flag-activity-${jobId}`;

	return (
		<React.Fragment>
			<span
				className={`job-flag-indicator${
					isActive ? '' : ' job-flag-indicator--empty'
				}${isPending ? ' job-flag-indicator--loading' : ''}`}
				{...(isActive
					? {
							// The pill is hover-only (decorative SVG); expose the active
							// flag labels as a text alternative so the indicator isn't
							// lost to keyboard/screen-reader users. Display-only span —
							// no role/onClick (nothing to activate).
							'aria-label': `Active flags: ${activeFlags
								.map((flag) => flag.label)
								.join(', ')}`,
							'data-tooltip-id': tooltipId,
							'data-tooltip-content': '',
						}
					: {})}
			>
				<BuildBackFlagIcon />
			</span>
			{isActive ? (
				<Tooltip
					id={tooltipId}
					className="escalated-banner-tooltip"
					positionStrategy="fixed"
					opacity={1}
					render={() => (
						<div>
							{activeFlags.map((flag) => (
								<div
									// created_at is unique per entry (BE-sorted oldest-first),
									// so it keys the line without relying on array index.
									key={`${flag.label}-${flag.created_at}`}
									className="escalated-banner-tooltip__title"
								>
									{flag.label}
								</div>
							))}
						</div>
					)}
				/>
			) : (
				''
			)}
		</React.Fragment>
	);
};

export default FlagActivityIndicator;
